import { useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import { router } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { DEFAULT_REGION_POLICIES } from '@ca/common'
import { trpc } from 'api'
import type { CustomerPolicyPatch } from '@ca/common'
import { useToast } from '@ui/toasts/useToastManager'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import Button from '@ui/buttons/Button'
import { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import useCreateCustomerStore from './useCreateCustomerStore'
import useGetAuthStrategy from 'hooks/useGetAuthStrategy'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import CreateCustomerDetails from './CreateCustomerDetails'
import CreateCustomerModal from './CreateCustomerModal'
import { CreateStepKey } from './types'
import {
  buildCustomerCreatePayload,
  createStepValidationContext,
  getErrorSections,
} from './utils'

export default function CreateCustomerScreen() {
  const { t } = useTranslation(['ui', 'auth', 'toasts'])
  const toast = useToast()
  const { data, reset } = useCreateCustomerStore()
  const getAuthStrategy = useGetAuthStrategy()
  const config = useRemoteConfig()

  useEffect(() => {
    reset()
  }, [reset])

  const [openStep, setOpenStep] = useState<CreateStepKey | null>('personal')
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [errorSections, setErrorSections] = useState<Set<CreateStepKey>>(
    () => new Set()
  )

  const validationCtx = useMemo(
    () =>
      createStepValidationContext(
        data,
        config?.region.policies ?? DEFAULT_REGION_POLICIES,
        config?.region?.vat !== 'hidden'
      ),
    [data, config?.region.policies, config?.region?.vat]
  )

  const currentErrors = useMemo(
    () => getErrorSections(data, validationCtx),
    [data, validationCtx]
  )
  const hasErrors = currentErrors.size > 0

  const createAccount = trpc.customer.create.useMutation({
    onError: (err) => {
      toast({
        title: t('toasts:error.createAccount.title'),
        text: err.message,
      })
    },
    onSuccess: () => {
      reset()
      router.replace('/staff/customers')
    },
  })

  const handleSubmit = () => {
    setHasSubmitted(true)

    if (hasErrors) {
      setErrorSections(currentErrors)
      toast({
        variant: 'error',
        title: t('toasts:error.createAccount.title'),
        text: t('toasts:error.createAccount.incomplete'),
      })
      return
    }
    setErrorSections(new Set())

    const customer = buildCustomerCreatePayload(
      data,
      config?.region.countryCode
    )

    if (!customer) {
      toast({
        title: t('toasts:error.createAccount.title'),
        text: t('auth:create.validation.incomplete'),
      })
      return
    }

    createAccount.mutate({
      customer,
      strategy: getAuthStrategy(customer),
      policies: (data.policies ?? {}) as CustomerPolicyPatch,
    })
  }

  const handleOpenStep = (step: CreateStepKey) => {
    setErrorSections((prev) => {
      if (!prev.has(step)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(step)
      return next
    })
    setOpenStep(step)
  }

  const closeModal = () => setOpenStep(null)

  return (
    <>
      <Page stage="staff" back>
        <Header
          title={t('auth:create.account.title')}
          desc={t('auth:create.account.desc')}
        />
        <CreateCustomerDetails
          onOpenStep={handleOpenStep}
          errorSections={errorSections}
        />
        <View className="flex-row justify-center pt-16">
          <Button
            variant={hasSubmitted ? 'filled' : 'outlined'}
            label={t('ui:submit')}
            iconEnd="arrow-narrow-right"
            loading={createAccount.isPending}
            disabled={hasSubmitted && hasErrors}
            onPress={handleSubmit}
          />
        </View>
        <SpaceButtons />
      </Page>

      {openStep !== null && (
        <CreateCustomerModal
          key={openStep}
          open
          close={closeModal}
          initialStep={openStep}
          onAllConfirmed={closeModal}
          errorSections={errorSections}
        />
      )}
    </>
  )
}
